/**
 * ConsentCapture.js
 *
 * Surgical consent form. Supports three consent types (surgical,
 * anaesthesia, blood transfusion) with template text, patient /
 * witness signatures, and emergency verbal consent flow.
 */
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import { surgeryService } from '../../../services/surgeryService';

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#212121',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#757575',
    marginBottom: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  consentText: {
    backgroundColor: '#FAFAFA',
    padding: '14px 16px',
    borderRadius: 6,
    border: '1px solid #EEEEEE',
    fontSize: '0.82rem',
    lineHeight: 1.6,
    color: '#424242',
    maxHeight: 180,
    overflowY: 'auto',
  },
  checkRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginBottom: 8,
  },
  signatureInput: {
    '& input': {
      fontStyle: 'italic',
      fontFamily: '"Georgia", serif',
      fontSize: '1.05rem',
      letterSpacing: '0.02em',
    },
  },
  verbalNotice: {
    padding: '10px 14px',
    backgroundColor: '#FFF8E1',
    borderRadius: 6,
    border: '1px solid #FFE082',
    fontSize: '0.82rem',
    fontWeight: 500,
    color: '#E65100',
    marginBottom: 16,
  },
  contextStrip: {
    display: 'flex',
    gap: 24,
    padding: '10px 16px',
    backgroundColor: '#FAFAFA',
    borderRadius: 6,
    marginBottom: 20,
    border: '1px solid #EEEEEE',
  },
  contextItem: {
    fontSize: '0.82rem',
    color: '#424242',
  },
  contextLabel: {
    color: '#9E9E9E',
    marginRight: 6,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 16,
    borderTop: '1px solid #F5F5F5',
  },
}));

const CONSENT_TEMPLATES = {
  surgical: `I, the undersigned patient, hereby consent to the performance of the surgical procedure described above. The nature and purpose of the procedure, along with its potential risks, benefits, and alternatives, have been explained to me in a language I understand. I understand that no guarantees have been made regarding the outcome. I have had the opportunity to ask questions and all my questions have been answered to my satisfaction. I voluntarily consent to the procedure and authorize the surgical team to perform the operation.`,
  anaesthesia: `I, the undersigned patient, hereby consent to the administration of anaesthesia as deemed appropriate by the anaesthesia team for the planned surgical procedure. The type of anaesthesia, potential risks (including but not limited to allergic reactions, nausea, sore throat, and rare complications), and alternatives have been explained to me. I understand that the anaesthesia team may need to change the planned anaesthetic technique if circumstances require. I voluntarily give my consent.`,
  blood_transfusion: `I, the undersigned patient, hereby consent to the transfusion of blood and/or blood products as deemed medically necessary by the treating team. The potential risks of blood transfusion, including but not limited to allergic reactions, transfusion reactions, and transmission of infections, have been explained to me. I understand that alternatives to blood transfusion may exist but may not be suitable in all circumstances. I voluntarily give my consent.`,
};

const ConsentCapture = ({ surgeryId, procedureName, surgeonName, patientName, onSave, readOnly }) => {
  const classes = useStyles();

  const [consentType, setConsentType] = useState('surgical');
  const [risksExplained, setRisksExplained] = useState(false);
  const [alternativesExplained, setAlternativesExplained] = useState(false);
  const [patientSignature, setPatientSignature] = useState('');
  const [witnessName, setWitnessName] = useState('');
  const [witnessSignature, setWitnessSignature] = useState('');
  const [emergencyVerbal, setEmergencyVerbal] = useState(false);
  const [verbalWitness1, setVerbalWitness1] = useState('');
  const [verbalWitness2, setVerbalWitness2] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load saved consent
  useEffect(() => {
    if (!surgeryId) return;
    surgeryService.listConsents(surgeryId).then((res) => {
      const consents = res?.data?.consents;
      if (!consents || consents.length === 0) return;
      const latest = consents[consents.length - 1];
      if (latest.consent_type) setConsentType(latest.consent_type);
      if (latest.is_verbal_consent) setEmergencyVerbal(true);
      if (latest.witness_name) setWitnessName(latest.witness_name);
      try {
        const notes = typeof latest.notes === 'string' ? JSON.parse(latest.notes) : latest.notes;
        if (notes) {
          if (notes.risks_explained !== undefined) setRisksExplained(notes.risks_explained);
          if (notes.alternatives_explained !== undefined) setAlternativesExplained(notes.alternatives_explained);
          if (notes.patient_signature) setPatientSignature(notes.patient_signature);
          if (notes.witness_signature) setWitnessSignature(notes.witness_signature);
          if (notes.verbal_witness_1) setVerbalWitness1(notes.verbal_witness_1);
          if (notes.verbal_witness_2) setVerbalWitness2(notes.verbal_witness_2);
        }
      } catch {}
    }).catch(() => {});
  }, [surgeryId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await surgeryService.createConsent({
        surgery_id: surgeryId,
        consent_type: consentType,
        procedure_name: procedureName,
        surgeon_name: surgeonName,
        patient_name: patientName,
        risks_explained: risksExplained,
        alternatives_explained: alternativesExplained,
        emergency_verbal_consent: emergencyVerbal,
        patient_signature: emergencyVerbal ? null : patientSignature,
        witness_name: emergencyVerbal ? null : witnessName,
        witness_signature: emergencyVerbal ? null : witnessSignature,
        verbal_witness_1: emergencyVerbal ? verbalWitness1 : null,
        verbal_witness_2: emergencyVerbal ? verbalWitness2 : null,
      });
      setSnackbar({ open: true, message: 'Consent saved', severity: 'success' });
      if (onSave) onSave();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save consent', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper className={classes.root} elevation={1}>
      <Typography className={classes.title}>Surgical Consent</Typography>
      <Typography className={classes.subtitle}>Informed consent documentation</Typography>

      {/* Context strip */}
      <div className={classes.contextStrip}>
        <span className={classes.contextItem}>
          <span className={classes.contextLabel}>Procedure:</span>
          {procedureName || '--'}
        </span>
        <span className={classes.contextItem}>
          <span className={classes.contextLabel}>Surgeon:</span>
          {surgeonName || '--'}
        </span>
        <span className={classes.contextItem}>
          <span className={classes.contextLabel}>Patient:</span>
          {patientName || '--'}
        </span>
      </div>

      {/* ── Consent Type ────────────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Consent Type</Typography>
        <FormControl component="fieldset" disabled={readOnly}>
          <RadioGroup
            row
            value={consentType}
            onChange={(e) => setConsentType(e.target.value)}
          >
            <FormControlLabel value="surgical" control={<Radio size="small" color="primary" />} label={<span style={{ fontSize: '0.82rem' }}>Surgical</span>} />
            <FormControlLabel value="anaesthesia" control={<Radio size="small" color="primary" />} label={<span style={{ fontSize: '0.82rem' }}>Anaesthesia</span>} />
            <FormControlLabel value="blood_transfusion" control={<Radio size="small" color="primary" />} label={<span style={{ fontSize: '0.82rem' }}>Blood Transfusion</span>} />
          </RadioGroup>
        </FormControl>
      </div>

      {/* ── Consent Text ────────────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Consent Declaration</Typography>
        <div className={classes.consentText}>
          {CONSENT_TEMPLATES[consentType]}
        </div>
      </div>

      {/* ── Acknowledgements ────────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Acknowledgements</Typography>
        <div className={classes.checkRow}>
          <FormControlLabel
            control={<Checkbox checked={risksExplained} onChange={(e) => !readOnly && setRisksExplained(e.target.checked)} color="primary" size="small" disabled={readOnly} />}
            label={<span style={{ fontSize: '0.82rem' }}>Risks explained to patient</span>}
          />
          <FormControlLabel
            control={<Checkbox checked={alternativesExplained} onChange={(e) => !readOnly && setAlternativesExplained(e.target.checked)} color="primary" size="small" disabled={readOnly} />}
            label={<span style={{ fontSize: '0.82rem' }}>Alternatives explained to patient</span>}
          />
        </div>
      </div>

      {/* ── Verbal consent toggle ───────────────────────── */}
      <div className={classes.section}>
        <FormControlLabel
          control={<Checkbox checked={emergencyVerbal} onChange={(e) => !readOnly && setEmergencyVerbal(e.target.checked)} color="secondary" size="small" disabled={readOnly} />}
          label={<span style={{ fontSize: '0.82rem', fontWeight: 500 }}>Emergency Verbal Consent</span>}
        />
      </div>

      {/* ── Signatures / Verbal Witnesses ────────────────── */}
      {!emergencyVerbal ? (
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Signatures</Typography>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Patient Signature (Type Name)" type />
                <div className={classes.signatureInput}>
                  <TextField
                    name="patientSignature"
                    value={patientSignature}
                    onChange={(e) => !readOnly && setPatientSignature(e.target.value)}
                    disabled={readOnly}
                    placeholder="Patient types full name"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Witness Name" />
                <TextField
                  name="witnessName"
                  value={witnessName}
                  onChange={(e) => !readOnly && setWitnessName(e.target.value)}
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Witness Signature (Type Name)" />
                <div className={classes.signatureInput}>
                  <TextField
                    name="witnessSignature"
                    value={witnessSignature}
                    onChange={(e) => !readOnly && setWitnessSignature(e.target.value)}
                    disabled={readOnly}
                    placeholder="Witness types full name"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </div>
      ) : (
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>Verbal Consent Witnesses</Typography>
          <div className={classes.verbalNotice}>
            Written consent to be obtained within 24 hours
          </div>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Witness 1 Name" type />
                <TextField
                  name="verbalWitness1"
                  value={verbalWitness1}
                  onChange={(e) => !readOnly && setVerbalWitness1(e.target.value)}
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Label name="Witness 2 Name" type />
                <TextField
                  name="verbalWitness2"
                  value={verbalWitness2}
                  onChange={(e) => !readOnly && setVerbalWitness2(e.target.value)}
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}

      {/* ── Footer ──────────────────────────────────────── */}
      {!readOnly && (
        <div className={classes.footer}>
          <button
            type="button"
            className="btn btn-sm sms-info-btn px-4"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Consent'}
          </button>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ConsentCapture;
